import { toast } from "react-toastify";
import { requestBackendReportExport } from "./backendReportExport";

export const PLACEMENT_BIDS_REPORT_TYPE = "PLACEMENT_BIDS";
const PLACEMENT_CUBE = "ClientDailyPlacementAmazonTable";

export const PLACEMENT_BIDS_COLUMN_ID_TO_API_KEY = {
  campaignNameDimension: "campaignNameDimension",
  placementDimension: "placementDimension",
  bidvalue: "bid",
  stratagy: "stratagy",
  cost: "cost",
  sales14d: "sales14d",
  conversions14d: "conversions14d",
  roas: "roas",
  ctr: "ctr",
  cpc: "cpc",
  dbr: "drrBurnRate",
  burnrate: "burnRate",
};

export function formatBurnRateDate(date) {
  const selectedDate = new Date(date);
  return `${selectedDate.getDate()}-${parseInt(selectedDate.getMonth() + 1, 10)}-${selectedDate.getFullYear()}`;
}

function buildStatRangesFromStatFilters(statFilters = []) {
  const rangesByField = {};

  statFilters.forEach((filter) => {
    const field = (filter?.field_key ?? "").replace(
      `${PLACEMENT_CUBE}.`,
      ""
    );
    if (!field) return;

    if (!rangesByField[field]) {
      rangesByField[field] = { field, min: 0, max: null };
    }

    if (filter.operator === "GREATER_THAN_EQUAL_TO") {
      rangesByField[field].min = Number(filter.value);
    }
    if (filter.operator === "LESS_THAN_EQUAL_TO") {
      rangesByField[field].max = Number(filter.value);
    }
  });

  return Object.values(rangesByField).filter(
    ({ min, max }) => min !== 0 || max !== null
  );
}

export function getPlacementBidsVisibleColumnKeys(dataHeads = []) {
  return dataHeads
    .map((head) => PLACEMENT_BIDS_COLUMN_ID_TO_API_KEY[head.id] ?? head.id)
    .filter(Boolean);
}

export function buildPlacementBidsExportQueryBody({
  cubeQueryMeasures,
  cubeQueryDimensions,
  cubeQueryOrder,
  cubeQueryFilters,
  cubeQueryTimeDimensions,
  calendarDate,
  strategy = [],
  targetingType = [],
  statFilters = [],
}) {
  const startDate = calendarDate?.[0]?.startDate ?? new Date();
  const endDate = calendarDate?.[0]?.endDate ?? new Date();
  const today = new Date();

  const timeDimensions = (cubeQueryTimeDimensions ?? []).map((td) => ({
    dimension: td.dimension,
    date_range: td.dateRange ?? td.compareDateRange?.[0] ?? [],
  }));

  return {
    cube_query: {
      measures: cubeQueryMeasures,
      dimensions: cubeQueryDimensions,
      order: cubeQueryOrder,
      filters: cubeQueryFilters,
      time_dimensions: timeDimensions.length
        ? timeDimensions
        : [
            {
              dimension: `${PLACEMENT_CUBE}.date`,
              date_range: [
                formatIsoDate(startDate),
                formatIsoDate(endDate),
              ],
            },
          ],
    },
    burn_rate_dates: {
      drr_start: formatBurnRateDate(startDate),
      drr_end: formatBurnRateDate(endDate),
      today: formatBurnRateDate(today),
    },
    client_filters: {
      strategy,
      targeting_type: targetingType,
      stat_ranges: buildStatRangesFromStatFilters(statFilters),
    },
  };
}

function formatIsoDate(date) {
  const selectedDate = new Date(date);
  const offset = selectedDate.getTimezoneOffset();
  const adjusted = new Date(selectedDate.getTime() - offset * 60 * 1000);
  return adjusted.toISOString().split("T")[0];
}

/**
 * PLACEMENT_BIDS_WRAPPER_COLUMN_KEY_MAP
 * --------------------------------------
 * Identity-ish map for wrapper column keys used in export frontend_config.
 */
export const PLACEMENT_BIDS_WRAPPER_COLUMN_KEY_MAP = {
  bid_adjustment_value: "bid_adjustment_value",
  campaign_bid_strategy: "campaign_bid_strategy",
};

/** Accepts either an array of column config objects or plain string keys.
 * Prefer display name key (innerKeys.productName) over id key for export columns.
 */
export function getPlacementBidsWrapperVisibleColumnKeys(columns = []) {
  return columns
    .map((column) => {
      if (typeof column === "string") {
        return PLACEMENT_BIDS_WRAPPER_COLUMN_KEY_MAP[column] ?? column;
      }
      const key = column?.innerKeys?.productName ?? column?.key;
      return PLACEMENT_BIDS_WRAPPER_COLUMN_KEY_MAP[key] ?? key;
    })
    .filter(Boolean);
}

/**
 * Builds the export request body for the v2 (wrapper-query) placement bids
 * report. `report_type` stays PLACEMENT_BIDS.
 */
export function buildPlacementBidsWrapperExportQueryBody({ query }) {
  return {
    query: { ...query },
  };
}

export async function exportPlacementBidsReport({
  clientId,
  userPlatform = "amazon",
  triggeredBy,
  totalCount,
  queryBody,
  visibleColumnKeys,
  frontendConfig = {},
  setIsExporting = () => {},
  onExportSuccess = () => {},
}) {
  try {
    setIsExporting(true);

    await requestBackendReportExport({
      clientId,
      userPlatform,
      reportType: PLACEMENT_BIDS_REPORT_TYPE,
      totalCount,
      triggeredBy,
      queryBody,
      frontendConfig: {
        columns: visibleColumnKeys,
        ...frontendConfig,
      },
    });

    onExportSuccess();
  } catch (error) {
    console.error(error);
    toast.error(
      error?.response?.data?.message ?? "Failed to submit export request."
    );
  } finally {
    setIsExporting(false);
  }
}
